// Список запусков агентов с раскрытием расшифровки.
import { useState } from 'react'
import { api } from '../api.ts'
import {
  JOB_STATUS_LABELS,
  JOB_STATUS_TONES,
  JOB_TRIGGER_LABELS,
  formatCount,
  formatDateTime,
  formatDuration,
  formatUsd,
  jobDurationMs,
  timeAgo,
} from '../lib/format.ts'
import { useTicker } from '../lib/hooks.ts'
import { TaskLink } from '../router.tsx'
import { useLive, useNow } from '../store.ts'
import { toast, toastError } from '../toast.ts'
import type { Job } from '../types.ts'
import { AgentLabel } from './AgentAvatar.tsx'
import { Icon } from './Icon.tsx'
import { JobTranscript } from './JobTranscript.tsx'
import { Badge, Button, Empty, QueuedDot, cx } from './ui.tsx'

export function JobStatusBadge({ status }: { status: Job['status'] }) {
  return (
    <Badge tone={JOB_STATUS_TONES[status]} dot={status === 'running' || status === 'failed' || status === 'waiting'}>
      {status === 'queued' ? <QueuedDot className="h-1.5 w-1.5" /> : null}
      {JOB_STATUS_LABELS[status] ?? status}
    </Badge>
  )
}

export function JobList({
  jobs,
  showAgent = true,
  showTask = false,
  emptyText = 'Запусков пока не было',
  initiallyOpen,
}: {
  jobs: Job[]
  showAgent?: boolean
  showTask?: boolean
  emptyText?: string
  initiallyOpen?: number | null
}) {
  const [open, setOpen] = useState<Set<number>>(() => new Set(initiallyOpen ? [initiallyOpen] : []))
  const anyRunning = jobs.some((j) => j.status === 'running')
  const tick = useTicker(1000, anyRunning)
  const now = useNow()

  if (!jobs.length) return <Empty icon="bolt">{emptyText}</Empty>

  const toggle = (id: number) =>
    setOpen((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const runners = useLive((s) => s.runners)
  const cancel = async (j: Job) => {
    if (!window.confirm(`Остановить запуск #${j.id}?`)) return
    try {
      await api.post(`/api/jobs/${j.id}/cancel`)
      toast(`Запуск #${j.id} остановлен`, 'success')
    } catch (e) {
      toastError(e, 'Не удалось остановить запуск')
    }
  }

  // машина запуска — понятным именем (страница «Машины»), а не id раннера
  const machine = (id: string | null) => (id ? runners.find((r) => r.id === id) : undefined)

  return (
    <ul className="flex flex-col gap-1.5">
      {jobs.map((j) => {
        const provider = j.provider ?? (machine(j.runner_id)?.labels.includes('codex') || j.session_id?.startsWith('codex:') ? 'codex' : 'claude')
        const model = provider === 'codex' && /^(opus|sonnet|haiku|claude)/.test(j.model ?? '') ? null : j.model
        const modelLabel = `${provider === 'codex' ? 'Codex' : 'Claude'}${model ? ` · ${model}` : ''}`
        const isOpen = open.has(j.id)
        const dur = jobDurationMs(j, Math.max(tick, now))
        return (
          <li key={j.id} className={cx('rounded-card border bg-surface shadow-card', isOpen ? 'border-line-strong' : 'border-line')}>
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 text-[13px]">
              <button
                type="button"
                onClick={() => toggle(j.id)}
                aria-expanded={isOpen}
                aria-label={isOpen ? `Свернуть запуск #${j.id}` : `Показать расшифровку запуска #${j.id}`}
                className="flex min-w-0 items-center gap-2 text-left"
              >
                <Icon name="chevronRight" size={14} className={cx('shrink-0 text-subtle transition-transform', isOpen && 'rotate-90')} />
                <span className="tnum font-mono text-[11.5px] text-muted">#{j.id}</span>
              </button>
              {showAgent ? <AgentLabel id={j.agent_id} size="xs" link className="max-w-[min(180px,100%)]" /> : null}
              {showTask && j.task_id ? (
                <TaskLink id={j.task_id} className="font-mono text-[11.5px] text-muted hover:underline">
                  OMA-{j.task_id}
                </TaskLink>
              ) : null}
              <span className="text-muted">{JOB_TRIGGER_LABELS[j.trigger] ?? j.trigger}</span>
              {j.runner_id ? (
                <span className="inline-flex items-center gap-1 text-[12px] text-subtle" title="Машина, на которой шёл запуск">
                  <Icon name="server" size={12} />
                  {machine(j.runner_id)?.name ?? j.runner_id}
                </span>
              ) : null}
              {j.runner_id ? <span className="text-[12px] text-muted" title="Фактическая модель запуска">{modelLabel}</span> : null}
              <JobStatusBadge status={j.status} />
              {j.attempt > 1 ? <span className="text-[11px] text-warn">попытка {j.attempt}</span> : null}
              {/* цифры запуска: на узком экране переносятся внутри своей группы, а не за край карточки */}
              <span className="tnum ml-auto flex max-w-full shrink-0 flex-wrap items-center justify-end gap-x-3 gap-y-0.5 text-[12px] whitespace-nowrap text-muted">
                <span className="tnum" title="Длительность">
                  {formatDuration(dur)}
                </span>
                <span className="tnum" title="Стоимость">
                  {formatUsd(j.cost_usd)}
                </span>
                <span className="tnum" title="Ходов агента">
                  {j.turns} х.
                </span>
                <span
                  className="tnum hidden md:inline"
                  title={`Токены: вход ${j.input_tokens}, выход ${j.output_tokens}`}
                >
                  {formatCount(j.input_tokens + j.output_tokens)} ток.
                </span>
                <span className="tnum text-subtle" title={formatDateTime(j.created_at)}>
                  {timeAgo(j.created_at, now)}
                </span>
                {j.status === 'running' || j.status === 'queued' || j.status === 'waiting' ? (
                  <Button size="xs" variant="ghost" onClick={() => void cancel(j)} aria-label={`Остановить запуск #${j.id}`}>
                    Стоп
                  </Button>
                ) : null}
              </span>
            </div>
            {!isOpen && j.error ? (
              <div className="truncate border-t border-line bg-danger-soft px-3 py-1.5 font-mono text-[12px] text-danger">{j.error}</div>
            ) : null}
            {isOpen ? (
              <div className="min-w-0 border-t border-line px-3.5 py-3 max-md:px-3">
                <div className="mb-2.5 flex min-w-0 flex-wrap gap-x-4 gap-y-0.5 font-mono text-[11.5px] text-subtle [overflow-wrap:anywhere]">
                  {j.runner_id ? <span>модель: {modelLabel}{!model ? ' (точная модель не записана)' : ''}</span> : null}
                  {j.runner_id ? <span>раннер: {machine(j.runner_id)?.name ?? j.runner_id}{machine(j.runner_id)?.labels.includes('codex') ? ' (ChatGPT · Codex)' : ''}</span> : null}
                  {j.session_id ? <span className="font-mono">сессия: {j.session_id.slice(0, 12)}…</span> : null}
                  <span>создан: {formatDateTime(j.created_at)}</span>
                  {j.started_at ? <span>старт: {formatDateTime(j.started_at)}</span> : null}
                  {j.finished_at ? <span>финиш: {formatDateTime(j.finished_at)}</span> : null}
                </div>
                <JobTranscript jobId={j.id} />
              </div>
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}
